import { useState, type FormEvent } from 'react';
import { CalendarBlank, CheckCircle, Info, SpinnerGap, X } from '@phosphor-icons/react';
import { useRescheduleSession, type RescheduleResult } from '@/lib/sessions/useRescheduleSession';
import type { NeuraxInterview } from '@/lib/interviews/useInterviews';

// One-time reschedule: pick a new time in the future, within 7 days of the
// ORIGINAL interview date. The server (b2b_reschedule_interview) is the source
// of truth for every rule — the min/max attributes here are UX only. A
// successful reschedule also extends the candidate's license validity.

interface Props {
  session: NeuraxInterview;
  onClose: () => void;
  onRescheduled?: (result: RescheduleResult) => void;
}

export function RescheduleSessionDialog({ session, onClose, onRescheduled }: Props) {
  const reschedule = useRescheduleSession();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RescheduleResult | null>(null);

  const originalDate = session.interview_date ? new Date(session.interview_date) : null;
  const deadline = originalDate ? new Date(originalDate.getTime() + 7 * 24 * 60 * 60 * 1000) : null;

  function close() {
    if (reschedule.isPending) return;
    onClose();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!value) {
      setError('Pick the new interview time.');
      return;
    }
    try {
      const res = await reschedule.mutateAsync({
        sessionId: session.id,
        newDate: new Date(value).toISOString(),
      });
      setResult(res);
      onRescheduled?.(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  return (
    <div
      data-testid="reschedule-dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Reschedule interview"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-neurax-midnight p-6 shadow-glow">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-electric/50 via-neurax-plasma/50 to-transparent" />

        <button
          type="button"
          onClick={close}
          disabled={reschedule.isPending}
          className="absolute right-4 top-4 rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-4 flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-neurax-electric/30 to-neurax-plasma/30 text-neurax-electric">
            <CalendarBlank className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-white">Reschedule interview</h2>
            <p className="text-xs text-muted-foreground">
              {session.end_client?.name || 'Candidate'} ·{' '}
              {session.position_title ||
                (session.session_type === 'online_assessment' ? 'Assessment' : 'Interview')}
            </p>
          </div>
        </div>

        {result ? (
          <div data-testid="reschedule-success" className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-neurax-mint/20 bg-neurax-mint/10 p-4 text-sm text-neurax-mint">
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p>
                  Interview moved to <strong>{fmtDateTime(result.new_date)}</strong>.
                </p>
                {result.license_valid_until && (
                  <p className="mt-1 text-xs opacity-90">
                    The candidate's license now stays valid through{' '}
                    {fmtDate(result.license_valid_until)}.
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="flex items-start gap-2.5 rounded-xl border border-neurax-amber/20 bg-neurax-amber/[0.06] p-3 text-xs text-neurax-amber/90">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span data-testid="reschedule-one-time-note">
                You can reschedule this interview <strong>once</strong>. The new time must be within
                7 days of the original interview
                {deadline ? ` (by ${fmtDateTime(deadline.toISOString())})` : ''}. Need another
                change later? Contact F1Jobs.
              </span>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Current time
              </label>
              <p data-testid="reschedule-current-date" className="text-sm text-white">
                {originalDate ? fmtDateTime(originalDate.toISOString()) : '—'}
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                New time
              </label>
              <input
                type="datetime-local"
                data-testid="reschedule-datetime"
                required
                value={value}
                onChange={(e) => setValue(e.target.value)}
                min={toDatetimeLocal(new Date())}
                max={deadline ? toDatetimeLocal(deadline) : undefined}
                disabled={reschedule.isPending}
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white outline-none transition [color-scheme:dark] focus:border-neurax-electric/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-neurax-electric/30 disabled:opacity-50"
              />
              <p className="text-[11px] text-muted-foreground">
                The candidate's license validity extends automatically to cover the new date.
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="reschedule-submit"
              disabled={reschedule.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110 disabled:opacity-60"
            >
              {reschedule.isPending && <SpinnerGap className="h-4 w-4 animate-spin" />}
              Reschedule interview
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/** Format a Date for the datetime-local input (local time, yyyy-MM-ddTHH:mm). */
function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
